import { css } from '@emotion/react';
import { graphql, PageProps as BasePageProps, withPrefix } from 'gatsby';
import { ContactSection } from '@/components/general/ContactSection';
import { Layout } from '@/components/general/Layout';
import { PageHeader } from '@/components/general/PageHeader';
import { Seo } from '@/components/general/Seo';
import { ButtonLink } from '@/components/shared/ButtonLink';
import { pagesPath } from '@/lib/pagesPath';
import { fadeIn } from '@/styles/keyframes';
import { getScreen } from '@/styles/media-query';
import { Faq } from '@/types/cms-types';

export const query = graphql`
  query {
    allMicrocmsIpoServiceFaq {
      nodes {
        ipoServiceFaqId
        question
        answer
        category
      }
    }
  }
`;

type PageProps = {
  data: {
    allMicrocmsIpoServiceFaq: {
      nodes: Faq[];
    };
  };
} & BasePageProps;

const Page: React.FC<PageProps> = ({
  location,
  data: {
    allMicrocmsIpoServiceFaq: { nodes },
  },
}) => {
  const categories = Array.from(
    new Set(nodes.map((node) => node.category[0])),
  ).reverse();

  return (
    <Layout location={location}>
      <Seo
        title="よくある質問"
        description="【Uniforce】IPO準備クラウドのよくあるご質問。ご契約やサービスの内容に関してよくあるご質問を掲載しています。"
      />
      <PageHeader
        css={css`
          margin-bottom: 44px;

          @media ${getScreen('pc')} {
            margin-bottom: 80px;
          }
        `}
        en="FAQ"
        title="よくあるご質問"
      >
        <div
          css={css`
            pointer-events: none;
            position: absolute;
            right: calc(50% + 189px);
            bottom: -16px;

            @media ${getScreen('pc', '未満')} {
              width: 82px;
              right: calc(50% + 84px);
              bottom: -14px;
            }
          `}
        >
          <img
            css={css`
              max-width: 100%;
              height: auto;
            `}
            src={withPrefix('/images/faq/header-decoration.png')}
            width="165"
            height="165"
            alt=""
          />
        </div>
      </PageHeader>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 60px;

          @media ${getScreen('pc')} {
            gap: 72px;
          }
        `}
      >
        {categories.map((category, i) => (
          <section
            key={i}
            css={css`
              width: 100%;
              max-width: 591px;
              margin: 0 auto;
              padding: 0 30px;

              @media ${getScreen('pc')} {
                padding: 0;
              }
            `}
          >
            <h2
              css={css`
                text-align: center;
                font: normal normal bold 18px/26px 'Noto Sans JP', sans-serif;
                letter-spacing: 0.72px;
                color: #00b3a7;
                margin-bottom: 24px;

                @media ${getScreen('pc')} {
                  font: normal normal bold 22px/32px 'Noto Sans JP', sans-serif;
                  letter-spacing: 1.52px;
                  margin-bottom: 32px;
                }
              `}
            >
              {category}
            </h2>
            <ul>
              {nodes
                .filter((node) => category === node.category[0])
                .map((node) => (
                  <li key={node.ipoServiceFaqId}>
                    <details>
                      <summary
                        css={css`
                          font: normal normal normal 14px/24px 'Noto Sans JP',
                            sans-serif;
                          letter-spacing: 0.22px;
                          color: #070707;
                          border-bottom: 1px solid #ebeff1;
                          display: block;
                          position: relative;
                          outline: none;
                          padding: 8px 32px 8px 8px;

                          @media ${getScreen('pc')} {
                            font: normal normal normal 14px/28px 'Noto Sans JP',
                              sans-serif;
                            letter-spacing: 0.25px;
                            padding: 16px 36px 16px 24px;
                          }

                          &::-webkit-details-marker {
                            padding: 15px;
                          }
                        `}
                      >
                        {node.question}
                        <img
                          css={css`
                            position: absolute;
                            right: 12px;
                            top: 50%;
                            transform: translateY(-50%);

                            [open] & {
                              transform: translateY(-50%) rotate(180deg);
                            }
                          `}
                          src={withPrefix('/images/faq/faq-arrow.svg')}
                          width="9"
                          height="5"
                          alt=""
                        />
                      </summary>
                      <div
                        css={css`
                          font: normal normal normal 14px/24px 'Noto Sans JP',
                            sans-serif;
                          letter-spacing: 0.22px;
                          color: #070707;
                          border-bottom: 1px solid #ebeff1;
                          padding: 12px 8px 12px 24px;

                          @media ${getScreen('pc')} {
                            font: normal normal normal 14px/28px 'Noto Sans JP',
                              sans-serif;
                            letter-spacing: 0.25px;
                            padding: 16px 36px 16px 16px;
                            margin-left: 16px;
                          }

                          [open] & {
                            animation: ${fadeIn} 0.3s ease both;
                          }
                        `}
                        dangerouslySetInnerHTML={{ __html: node.answer }}
                      />
                    </details>
                  </li>
                ))}
            </ul>
          </section>
        ))}
      </div>
      <section
        css={css`
          margin-top: 60px;

          @media ${getScreen('pc')} {
            margin-top: 80px;
          }
        `}
      >
        <h2
          css={css`
            text-align: center;
            font: normal normal 500 16px/24px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.13px;
            color: #2b2c40;
            margin-bottom: 24px;

            @media ${getScreen('pc')} {
              font: normal normal bold 20px/29px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.16px;
            }
          `}
        >
          その他のお問合せはこちらまで
        </h2>
        <ButtonLink
          css={css`
            margin: 0 30px;

            @media ${getScreen('pc')} {
              width: 237px;
              margin: 0 auto;
            }
          `}
          to={pagesPath.contact.$url()}
          variant="more"
        >
          お問合せページへ
        </ButtonLink>
      </section>
      <ContactSection
        css={css`
          margin-top: 88px;

          @media ${getScreen('pc')} {
            margin-top: 128px;
          }
        `}
        title1="Uniforce IPO準備クラウドで"
        title2="「IPOの準備を効率的」"
        title3="に"
        title4="進めませんか？"
        text="IPO準備クラウドの機能詳細や無料デモのご相談はこちら"
      />
    </Layout>
  );
};

export default Page;
